import React, { useState } from 'react';
import { CheckCircle2, AlertTriangle } from 'lucide-react';
import { verifySpokenCode } from '@/lib/claimRecords';
import vectors from '@/lib/claimRecordVectors.json';

export function SpokenCodePanel() {
  const [code, setCode] = useState('');
  const [expected, setExpected] = useState(null);
  const [result, setResult] = useState(null);
  const load = (c) => { setCode(c.code); setExpected(c.expected); setResult(null); };
  const run = () => setResult(verifySpokenCode(code));
  return (
    <section className="rounded-2xl border border-white/10 bg-white/[0.02] p-6" data-testid="spoken-code-panel">
      <h2 className="text-lg font-semibold mb-1">2. Check a spoken code</h2>
      <p className="text-sm text-gray-400 mb-4">Type a code as it was heard on a call. A failed check means "please read it back," never a rejected claim.</p>
      <div className="flex flex-wrap items-center gap-2 mb-5" data-testid="code-examples">
        <span className="text-xs text-gray-500 mr-1">Load example:</span>
        {vectors.codeChecks.map((c) => (
          <button key={c.id} type="button" onClick={() => load(c)} className="rounded-full border border-white/15 px-3 py-1 text-xs text-gray-200 hover:border-amber-400/60 hover:text-white transition-colors" data-testid={`code-load-${c.id}`}>{c.id}</button>
        ))}
      </div>
      <div className="flex flex-col sm:flex-row gap-3 sm:items-end">
        <label className="block flex-1 max-w-xs">
          <span className="text-xs uppercase tracking-widest text-gray-400">Spoken code</span>
          <input value={code} onChange={(e) => { setCode(e.target.value); setExpected(null); setResult(null); }} spellCheck={false} className="mt-1.5 w-full rounded-xl border border-white/10 bg-[#0a0a0a] p-2.5 font-mono text-lg tracking-[0.25em] text-gray-200 focus:border-amber-500/60 focus:outline-none" data-testid="code-input" />
        </label>
        <button type="button" onClick={run} disabled={!code.trim()} className="rounded-full bg-white text-black px-6 py-2.5 text-sm font-semibold hover:bg-gray-200 transition-colors disabled:opacity-40" data-testid="code-check-button">Check</button>
      </div>
      {result && (
        <div className={`mt-5 rounded-xl border px-4 py-3 text-sm flex items-start gap-2 ${result.ok ? 'border-emerald-500/40 bg-emerald-500/10 text-emerald-200' : 'border-amber-500/40 bg-amber-500/10 text-amber-100'}`} data-testid="code-result">
          {result.ok ? <CheckCircle2 className="w-4 h-4 mt-0.5 shrink-0" /> : <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" />}
          <span>
            <span className="font-mono mr-2" data-testid="code-normalized">{result.code}</span>
            <span data-testid="code-reason">{result.reason}</span>
            {expected && <span className="ml-2 text-gray-300" data-testid="code-vector-match">Example expects {expected.ok ? 'valid' : 'read back'}: {expected.ok === result.ok ? 'match' : 'MISMATCH'}.</span>}
          </span>
        </div>
      )}
    </section>
  );
}
